import { useCallback } from 'react';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { ASYNC_BULK_GET_QUIZ_ME_QUERY } from '../../../../__generated__/chirp/com/hubspot/academy/gen/ai/skills/rpc/QuizMeRpc.dfc.new';
import { queryDataFromCache, updateCache } from '../../../utils/DataFetchingClientCache.new';
import { DIFFICULTY_LEVELS } from '../interactiveEducationConstants';
export const QuizMeDefaultState = {
  LESSON: {},
  SHORT_FORM_CONTENT: {},
  TRACK: {}
};
export const useQuizMeSessionConfig = create(set => Object.assign({}, QuizMeDefaultState, {
  deleteQuizState: ({
    contentType,
    contentId
  }) => {
    set(state => {
      const filteredState = Object.assign({}, state[contentType]);
      delete filteredState[contentId];
      return {
        [contentType]: filteredState
      };
    });
  },
  incrementQuestionWithAnswer: ({
    contentType,
    contentId,
    correct
  }) => {
    set(state => {
      const newState = Object.assign({}, state[contentType][contentId], {
        questionIndex: state[contentType][contentId].questionIndex + 1,
        correctAnswers: state[contentType][contentId].correctAnswers + (correct ? 1 : 0)
      });
      return {
        [contentType]: Object.assign({}, state[contentType], {
          [contentId]: newState
        })
      };
    });
  },
  initialiseQuiz: ({
    contentType,
    contentId
  }) => {
    set(state => ({
      [contentType]: Object.assign({}, state[contentType], {
        [contentId]: {
          questionIndex: 0,
          correctAnswers: 0
        }
      })
    }));
  },
  setQuizMeStatus: ({
    contentType,
    contentId,
    quizMeStatus
  }) => {
    set(state => ({
      [contentType]: Object.assign({}, state[contentType], {
        [contentId]: Object.assign({}, state[contentType][contentId], {
          quizMeStatus
        })
      })
    }));
  },
  setQuizMeTasks: ({
    contentType,
    videoContentId,
    tasks
  }) => {
    set(state => ({
      [contentType]: Object.assign({}, state[contentType], {
        [videoContentId]: Object.assign({}, state[contentType][videoContentId], {
          tasks
        })
      })
    }));
  },
  resetQuiz: ({
    contentType,
    videoContentId
  }) => {
    set(state => ({
      [contentType]: Object.assign({}, state[contentType], {
        [videoContentId]: Object.assign({}, state[contentType][videoContentId], {
          questionIndex: 0,
          correctAnswers: 0
        })
      })
    }));
  },
  setClickTimestamp: ({
    contentType,
    contentId,
    timestamp
  }) => {
    set(state => ({
      [contentType]: Object.assign({}, state[contentType], {
        [contentId]: Object.assign({}, state[contentType][contentId], {
          clickTimestamp: timestamp
        })
      })
    }));
  },
  setRenderedTimestamp: ({
    contentType,
    contentId,
    timestamp
  }) => {
    set(state => {
      var _state$contentType$co;
      if (((_state$contentType$co = state[contentType][contentId]) === null || _state$contentType$co === void 0 ? void 0 : _state$contentType$co.renderedTimestamp) !== undefined) {
        return state;
      }
      return {
        [contentType]: Object.assign({}, state[contentType], {
          [contentId]: Object.assign({}, state[contentType][contentId], {
            renderedTimestamp: timestamp
          })
        })
      };
    });
  },
  setDifficultyTasks: ({
    contentType,
    contentId,
    difficultyTasks
  }) => {
    set(state => {
      var _state$contentType$co2;
      return {
        [contentType]: Object.assign({}, state[contentType], {
          [contentId]: Object.assign({}, state[contentType][contentId], {
            difficultyTasks: Object.assign({}, (_state$contentType$co2 = state[contentType][contentId]) === null || _state$contentType$co2 === void 0 ? void 0 : _state$contentType$co2.difficultyTasks, difficultyTasks)
          })
        })
      };
    });
  },
  setSelectedDifficulty: ({
    contentType,
    contentId,
    selectedDifficulty
  }) => {
    set(state => ({
      [contentType]: Object.assign({}, state[contentType], {
        [contentId]: Object.assign({}, state[contentType][contentId], {
          selectedDifficulty
        })
      })
    }));
  }
}));
export const useQuizMeSession = () => {
  const {
    LESSON,
    SHORT_FORM_CONTENT,
    TRACK,
    deleteQuizState,
    incrementQuestionWithAnswer,
    initialiseQuiz,
    resetQuiz,
    setQuizMeTasks,
    setQuizMeStatus,
    setClickTimestamp,
    setRenderedTimestamp,
    setDifficultyTasks,
    setSelectedDifficulty
  } = useQuizMeSessionConfig();
  const getQuizState = useCallback((id, contentType) => {
    switch (contentType) {
      case 'LESSON':
        return LESSON[id];
      case 'SHORT_FORM_CONTENT':
        return SHORT_FORM_CONTENT[id];
      case 'TRACK':
        return TRACK[id];
      default:
        return undefined;
    }
  }, [LESSON, SHORT_FORM_CONTENT, TRACK]);
  const setQuizMeClickTimestamp = useCallback(({
    contentType,
    contentId
  }) => setClickTimestamp({
    contentType,
    contentId,
    timestamp: performance.now()
  }), [setClickTimestamp]);
  const getQuizMeRenderedTimestamp = useCallback(({
    contentType,
    contentId
  }) => {
    var _getQuizState;
    const existing = (_getQuizState = getQuizState(contentId, contentType)) === null || _getQuizState === void 0 ? void 0 : _getQuizState.renderedTimestamp;
    if (existing !== undefined) {
      return existing;
    }
    const timestamp = performance.now();
    setRenderedTimestamp({
      contentType,
      contentId,
      timestamp
    });
    return timestamp;
  }, [getQuizState, setRenderedTimestamp]);
  return {
    deleteQuizState,
    getQuizMeRenderedTimestamp,
    getQuizState,
    incrementQuestionWithAnswer,
    initialiseQuiz,
    resetQuiz,
    setDifficultyTasks,
    setQuizMeClickTimestamp,
    setQuizMeStatus,
    setQuizMeTasks,
    setSelectedDifficulty
  };
};
const flattenQuizQuestionsWithCorrelation = ({
  lessonContentId,
  quizTasks
}) => {
  if (quizTasks) {
    return quizTasks.tasks.flatMap(quizMeTaskItem => {
      var _quizMeTaskItem$quizR;
      return ((_quizMeTaskItem$quizR = quizMeTaskItem.quizResponse) === null || _quizMeTaskItem$quizR === void 0 || (_quizMeTaskItem$quizR = _quizMeTaskItem$quizR.quiz) === null || _quizMeTaskItem$quizR === void 0 ? void 0 : _quizMeTaskItem$quizR.questions.map(question => {
        var _quizMeTaskItem$quizR2;
        return Object.assign({}, question, {
          correlationId: (_quizMeTaskItem$quizR2 = quizMeTaskItem.quizResponse) === null || _quizMeTaskItem$quizR2 === void 0 ? void 0 : _quizMeTaskItem$quizR2.correlationId,
          contentId: lessonContentId
        });
      })) || [];
    });
  }
  return [];
};
export const useQuizMeHelpers = () => {
  const {
    getQuizState
  } = useQuizMeSession();
  const formatAsyncQuizMeQuestions = useCallback(({
    lessonContentId,
    contentType,
    initialQuizTasks,
    primaryContentId
  }) => {
    var _quizQuestions$questi;
    const quizSession = getQuizState(lessonContentId !== null && lessonContentId !== void 0 ? lessonContentId : primaryContentId, contentType);
    const quizQuestions = flattenQuizQuestionsWithCorrelation({
      lessonContentId,
      quizTasks: initialQuizTasks
    });
    const questionIndex = quizSession === null || quizSession === void 0 ? void 0 : quizSession.questionIndex;
    const currentCorrelationId = questionIndex !== undefined ? (_quizQuestions$questi = quizQuestions[questionIndex]) === null || _quizQuestions$questi === void 0 ? void 0 : _quizQuestions$questi.correlationId : undefined;
    return {
      currentCorrelationId,
      quizQuestions
    };
  }, [getQuizState]);
  return {
    formatAsyncQuizMeQuestions
  };
};
export const checkQuizMePollingStatus = (asyncQuizMe, hasError) => {
  if (!asyncQuizMe) {
    return {
      quizReady: false,
      pollingLoading: true,
      retryGeneration: false
    };
  }
  const {
    quizReady,
    pollingLoading,
    retryGeneration
  } = asyncQuizMe.tasks.reduce((prev, current) => {
    // Add 'NOT_FOUND' to the list of failed statuses, when BE has a fix more info: https://git.hubteam.com/HubSpot/LearningCenterUI/issues/9715
    if (hasError) {
      prev.retryGeneration = true;
    }
    if (current.status !== 'COMPLETED' && current.status !== 'FAILED') {
      prev.quizReady = false;
    }
    return prev;
  }, {
    quizReady: true,
    pollingLoading: false,
    retryGeneration: false
  });
  return {
    quizReady,
    pollingLoading,
    retryGeneration
  };
};
export const collectTaskIdsFromState = state => {
  const ids = [];
  if (state !== null && state !== void 0 && state.tasks) ids.push(state.tasks);
  if (state !== null && state !== void 0 && state.difficultyTasks) {
    DIFFICULTY_LEVELS.forEach(level => {
      const taskId = state.difficultyTasks[level];
      if (taskId) ids.push(taskId);
    });
  }
  return ids;
};
const DIFFICULTY_PREFERENCES_STORAGE_KEY = 'quiz-me-difficulty-preferences';
const useDifficultyPreferencesStore = create()(persist(set => ({
  preferences: {},
  setPreference: (contentId, difficulty) => set(state => ({
    preferences: Object.assign({}, state.preferences, {
      [contentId]: difficulty
    })
  })),
  clearPreference: contentId => set(state => {
    const updated = Object.assign({}, state.preferences);
    delete updated[contentId];
    return {
      preferences: updated
    };
  })
}), {
  name: DIFFICULTY_PREFERENCES_STORAGE_KEY
}));
export const useDifficultyPreference = contentId => useDifficultyPreferencesStore(state => state.preferences[contentId]);
export const persistDifficulty = (contentId, difficulty) => useDifficultyPreferencesStore.getState().setPreference(contentId, difficulty);
export const clearPersistedDifficulty = contentId => useDifficultyPreferencesStore.getState().clearPreference(contentId);
export const spliceQuizMeCache = ({
  oldTaskIds,
  newTaskIds,
  videoIndex,
  newTaskData
}) => {
  const existingData = queryDataFromCache(ASYNC_BULK_GET_QUIZ_ME_QUERY, {
    taskIds: oldTaskIds
  });
  if (!existingData) {
    return undefined;
  }
  const updatedTasks = [...existingData.tasks];
  updatedTasks[videoIndex] = newTaskData;
  updateCache(ASYNC_BULK_GET_QUIZ_ME_QUERY, draft => {
    draft.tasks = updatedTasks;
  }, {
    taskIds: newTaskIds
  });
  return updatedTasks;
};